// raw-fetch-ok-file: v0y4ge/voyage-fleet-management#676 -- pre-existing raw-fetch shadow API layer (studio-shell/coord-shell internal *-api.ts or identity/integrations-worker call), predates @v0y4ge/api-client convergence; tracked backlog, not a new violation.
import { useEffect, useMemo, useState } from "react";
import type { Session, SessionTenantMembership } from "@v0y4ge/studio-shell";
// Session-refresh role-collapse fix (2026-07-28): reuse the SAME canonical
// role-id -> studio-role-label map the shared api-client's `whoami()`
// transform (packages/api-contracts/src/settings.ts) already applies to
// `user.assignments` on a fresh sign-in. This host hook talks to the
// identity worker directly (raw fetch, not the api-client op — see the
// module doc below for why), so without this import it was a SECOND,
// divergent whoami parser that silently dropped `user.assignments`
// entirely on every reload; any persona whose role label wasn't one of
// App.tsx's hardcoded five Events labels collapsed to zero capabilities on
// refresh even though the identity worker returned a perfectly good
// session. Importing the map (never re-typing it locally) keeps this
// parser's normalization identical to the contract's, so the two paths
// cannot drift apart again.
import { CANONICAL_ROLE_ID_TO_STUDIO_ROLE } from "@v0y4ge/api-contracts";

// Re-export the canonical session types so any component can import `Session`
// from this module. Identity/whoami logic lives ONCE here in the platform —
// the per-tenant fork carries no whoami override.
export type { Session, SessionTenant, SessionTenantMembership, SessionUser } from "@v0y4ge/studio-shell";

export const NEUTRAL_SESSION: Session = {
  user: {
    id: "",
    email: "",
    displayName: "",
    initials: "",
    isMultiTenant: false,
  },
  tenant: {
    id: "",
    slug: "acme-events",
    displayName: "",
  },
  role: null,
};

const WHOAMI_TIMEOUT_MS = 8000;

export interface WhoamiState {
  session: Session;
  authenticated: boolean;
  loading: boolean;
  error: string | null;
  source: "identity-worker" | "neutral";
}

interface WhoamiResponse {
  authenticated?: boolean;
  capabilities?: unknown;
  memberships?: unknown;
  role?: unknown;
  platform_role?: unknown;
  user?: {
    id?: unknown;
    email?: unknown;
    display_name?: unknown;
    is_multi_tenant?: unknown;
    // #702: demo-persona sessions carry the persona's real CD Events role
    // LABEL here (e.g. "Event Coordinator"), distinct from the collapsed
    // top-level `role` MembershipRole slug (e.g. "member"). Omitted for
    // non-demo users -- see sessionFromWhoami's fallback chain below.
    role?: unknown;
    // Session-refresh role-collapse fix (2026-07-28): the identity worker's
    // real whoami wire shape (workers/identity/src/routes/auth.ts) carries
    // per-process CANONICAL role ids here (e.g. "queues.queue_manager"), the
    // exact same field the shared api-client's WhoamiResponse transform
    // reads and normalizes. Untyped on the wire (object of string->string);
    // narrowed in mapAssignments below.
    assignments?: unknown;
  };
  tenant?: {
    id?: unknown;
    slug?: unknown;
    display_name?: unknown;
    role?: unknown;
  };
}

export function useWhoami(identityBaseUrl: string | undefined, tenantSlug = "acme-events", enabled = true): WhoamiState {
  const baseUrl = useMemo(() => normalizeIdentityBaseUrl(identityBaseUrl), [identityBaseUrl]);
  const [state, setState] = useState<WhoamiState>({
    session: NEUTRAL_SESSION,
    authenticated: false,
    loading: Boolean(baseUrl),
    error: null,
    source: "neutral",
  });

  useEffect(() => {
    if (!enabled) {
      setState({
        session: NEUTRAL_SESSION,
        authenticated: false,
        loading: false,
        error: null,
        source: "neutral",
      });
      return;
    }
    if (!baseUrl) {
      setState({
        session: NEUTRAL_SESSION,
        authenticated: false,
        loading: false,
        error: null,
        source: "neutral",
      });
      return;
    }

    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, WHOAMI_TIMEOUT_MS);
    setState({
      session: NEUTRAL_SESSION,
      authenticated: false,
      loading: true,
      error: null,
      source: "neutral",
    });

    void fetch(`${baseUrl}/t/${encodeURIComponent(tenantSlug)}/auth/whoami`, {
      credentials: "include",
      headers: { accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({})) as WhoamiResponse;
        if (response.status === 401 || body.authenticated === false) {
          setState({
            session: NEUTRAL_SESSION,
            authenticated: false,
            loading: false,
            error: null,
            source: "neutral",
          });
          return;
        }
        if (!response.ok) throw new Error(`whoami HTTP ${response.status}`);
        if (body.authenticated !== true) {
          setState({
            session: NEUTRAL_SESSION,
            authenticated: false,
            loading: false,
            error: null,
            source: "neutral",
          });
          return;
        }
        setState({
          session: sessionFromWhoami(body),
          authenticated: true,
          loading: false,
          error: null,
          source: "identity-worker",
        });
      })
      .catch((error) => {
        if (controller.signal.aborted && !timedOut) return;
        setState({
          session: NEUTRAL_SESSION,
          authenticated: false,
          loading: false,
          error: timedOut ? "whoami timed out" : error instanceof Error ? error.message : "whoami could not be loaded",
          source: "neutral",
        });
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [baseUrl, tenantSlug, enabled]);

  return state;
}

function sessionFromWhoami(body: WhoamiResponse): Session {
  const email = stringValue(body.user?.email);
  const displayName = stringValue(body.user?.display_name) || email;
  // #702: body.user.role (the demo persona's real CD Events role label, e.g.
  // "Event Coordinator") is authoritative when present -- it must win over
  // the collapsed top-level body.role (the MembershipRole slug, e.g.
  // "member") so a reload hydrates the SAME role a fresh sign-in resolves.
  // Non-demo sessions never carry user.role, so this falls through to the
  // pre-existing slug/platform-admin/tenant-role chain unchanged.
  const role =
    stringValue(body.user?.role) ||
    stringValue(body.role) ||
    platformAdminRole(body.platform_role) ||
    stringValue(body.tenant?.role) ||
    null;
  const capabilities = stringArray(body.capabilities);
  const memberships = membershipArray(body.memberships);
  const assignments = mapAssignments(body.user?.assignments);

  return {
    user: {
      id: stringValue(body.user?.id) || NEUTRAL_SESSION.user.id,
      email,
      displayName,
      initials: initials(displayName || email),
      isMultiTenant: body.user?.is_multi_tenant === true || (memberships?.length ?? 0) > 1,
    },
    tenant: {
      id: stringValue(body.tenant?.id) || NEUTRAL_SESSION.tenant.id,
      slug: stringValue(body.tenant?.slug) || NEUTRAL_SESSION.tenant.slug,
      displayName: stringValue(body.tenant?.display_name) || NEUTRAL_SESSION.tenant.displayName,
    },
    role,
    ...(capabilities ? { capabilities } : {}),
    ...(memberships ? { memberships } : {}),
    ...(assignments ? { assignments } : {}),
  };
}

// Session-refresh role-collapse fix (2026-07-28): normalizes
// `body.user.assignments` (per-process CANONICAL role ids straight off the
// identity worker, e.g. `{ queues: "queues.queue_manager" }`) onto the
// consuming app's own role-name vocabulary, using the SAME
// `CANONICAL_ROLE_ID_TO_STUDIO_ROLE` map the shared api-client's
// `WhoamiResponse` transform (`@v0y4ge/api-contracts`) already applies on a
// fresh sign-in -- see that module's own comment for why an entry with no
// mapped equivalent is DROPPED rather than passed through: a bogus/unmapped
// assignment must never masquerade as resolvable and suppress the
// `user.role` fallback. An id already in display-vocabulary (no ".", e.g. a
// tenant-defined custom role name) passes through unchanged, mirroring the
// contract transform's own passthrough rule. Returns `undefined` (not `{}`)
// when nothing survives so callers see the same "no assignments" shape they
// already handle.
function mapAssignments(value: unknown): Record<string, string> | undefined {
  if (!value || typeof value !== "object") return undefined;
  const mapped: Record<string, string> = {};
  for (const [process, roleId] of Object.entries(value as Record<string, unknown>)) {
    if (typeof roleId !== "string" || !roleId) continue;
    const studioRole = CANONICAL_ROLE_ID_TO_STUDIO_ROLE[roleId];
    if (studioRole) {
      mapped[process] = studioRole;
    } else if (!roleId.includes(".")) {
      mapped[process] = roleId;
    }
  }
  return Object.keys(mapped).length ? mapped : undefined;
}

function normalizeIdentityBaseUrl(value: string | undefined): string {
  return (value ?? "").trim().replace(/\/t\/[^/]+\/auth\/whoami\/?$/, "").replace(/\/$/, "");
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const seen = new Set<string>();
  for (const item of value) {
    const normalized = stringValue(item);
    if (normalized) seen.add(normalized);
  }
  return [...seen];
}

function membershipArray(value: unknown): SessionTenantMembership[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const memberships: SessionTenantMembership[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const source = item as Record<string, unknown>;
    const id = stringValue(source.id);
    const slug = stringValue(source.slug);
    const name = stringValue(source.name);
    const studioHost = stringValue(source.studioHost);
    if (!id || !slug || !name || !studioHost || seen.has(id)) continue;
    seen.add(id);
    memberships.push({
      id,
      slug,
      name,
      studioHost,
      role: stringValue(source.role) || null,
    });
  }
  return memberships.length ? memberships : undefined;
}

function platformAdminRole(value: unknown): string {
  return value === "platform_admin" ? "platform_admin" : "";
}

function initials(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const nameParts = trimmed.includes("@") ? [trimmed.split("@")[0] ?? ""] : trimmed.split(/\s+/);
  const letters = nameParts.map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  return letters;
}
